import React, { useState, useEffect } from 'react';
import { 
  HiOutlineDatabase, HiOutlineX, HiOutlinePlus, HiOutlineTrash, 
  HiOutlineSave, HiOutlineOfficeBuilding, HiOutlineShieldCheck, HiOutlineLockClosed
} from 'react-icons/hi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getSubIndustryOptions, getIndustryConfig } from '../../config/industryConfig';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useOrg } from '../../context/OrgContext';
import { useAuth } from '../../context/AuthContext';

const DomainBuilder = () => {
  const { user } = useAuth();
  const { settings } = useOrg();
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  
  // Check if current user is platform super admin
  const isPlatformSuperAdmin = (user?.role === 'super_admin') || (user?.email === process.env.REACT_APP_PLATFORM_ADMIN_EMAIL);
  const orgName = settings?.name || settings?.brandName || user?.organizationId?.name || 'Current Organization';
  const orgSubIndustryId = settings?.subIndustryId || user?.organizationId?.subIndustryId || 'drag_chain';

  const SUB_MODULES_MAP = {
    gateGuard: [
      { key: 'gateGuardFields', label: 'Log Inward Material' }
    ],
    storeManager: [
      { key: 'inventoryFields', label: 'Unified Inventory' },
      { key: 'dispatchFields', label: 'Dispatch & Shipping' },
      { key: 'sbsLogsFields', label: 'Shortage, Buy & Sales' },
      { key: 'qcVerificationFields', label: 'Quality Logs Verification' },
      { key: 'assignmentFields', label: 'Task Assignment' }
    ],
    supervisor: [
      { key: 'machineAutomationFields', label: 'Machine Automation' },
      { key: 'buildQueueFields', label: 'Product Build Queue' },
      { key: 'assignedTasksFields', label: 'Assigned Tasks' }
    ],
    qualityChecker: [
      { key: 'qualityFields', label: 'Quality Inspection' }
    ],
    sales: [
      { key: 'salesFields', label: 'Sales & Orders' }
    ]
  };

  // Create dynamic nested modules from actual menus, ignoring admin/setting ones
  const modules = (settings?.menus || [])
    .filter(m => !['users', 'organizations', 'settings'].includes(m.key))
    .map(m => ({
      key: m.key,
      label: m.label,
      subModules: SUB_MODULES_MAP[m.key] || [{ key: `${m.key}Fields`, label: m.label + ' Form' }]
    }));

  const initialModule = modules.length > 0 && modules[0].subModules.length > 0 ? { parent: modules[0].label, ...modules[0].subModules[0] } : null;
  
  const [activeModule, setActiveModule] = useState(initialModule);
  const [expandedMenu, setExpandedMenu] = useState(modules.length > 0 ? modules[0].key : '');
  const [schemaFields, setSchemaFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const allOptions = getSubIndustryOptions();
    
    if (isPlatformSuperAdmin) {
      // Platform Admin / Super Admin: Show all SaaS industries and domain templates
      setIndustries(allOptions);
    } else {
      // Organization Admin: Strictly show ONLY their own organization domain
      const myConfig = getIndustryConfig(orgSubIndustryId);
      const myIndustry = allOptions.find(ind => ind.id === orgSubIndustryId) || {
        id: orgSubIndustryId,
        label: settings?.subIndustry || myConfig?.label || 'Drag Chain',
        color: myConfig?.color || '#f97316'
      };

      setIndustries([{
        ...myIndustry,
        label: `${orgName} (${myIndustry.label})`,
        rawLabel: myIndustry.label,
        orgName: orgName,
        isOrgSpecific: true
      }]);
    }
  }, [isPlatformSuperAdmin, orgSubIndustryId, orgName, settings]);

  const openIndustry = async (industry) => {
    setSelectedIndustry(industry);
    const initialMod = modules.length > 0 && modules[0].subModules.length > 0 ? { parent: modules[0].label, ...modules[0].subModules[0] } : null;
    setActiveModule(initialMod);
    if (modules.length > 0) setExpandedMenu(modules[0].key);
    if (initialMod) {
      await fetchSchema(industry.id, initialMod.key);
    }
  };

  const closeIndustry = () => {
    setSelectedIndustry(null);
    setSchemaFields([]);
  };

  const fetchSchema = async (industryId, moduleKey) => {
    setLoading(true);
    try {
      const res = await api.get(`/domain/schema/${industryId}`);
      // Find the specific module schema
      const moduleSchema = (res.data || []).find(s => s.moduleKey === moduleKey);
      if (moduleSchema) {
        setSchemaFields(moduleSchema.fields || []);
      } else {
        setSchemaFields([]);
      }
    } catch (error) {
      toast.error('Failed to fetch schema');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleChange = async (parentModule, subModule) => {
    setActiveModule({ parent: parentModule.label, ...subModule });
    if (selectedIndustry) {
      await fetchSchema(selectedIndustry.id, subModule.key);
    }
  };

  const toggleMenu = (menuKey) => {
    setExpandedMenu(expandedMenu === menuKey ? '' : menuKey);
  };

  const handleAddField = () => {
    const newField = {
      key: `field_${Date.now()}`,
      label: 'New Field',
      type: 'text',
      placeholder: '',
      required: false,
      options: []
    };
    setSchemaFields([...schemaFields, newField]);
  };

  const handleRemoveField = (index) => {
    const updated = [...schemaFields];
    updated.splice(index, 1);
    setSchemaFields(updated);
  };

  const handleFieldChange = (index, key, value) => {
    const updated = [...schemaFields];
    updated[index][key] = value;
    setSchemaFields(updated);
  };

  const handleSaveSchema = async () => {
    if (!selectedIndustry || !activeModule) return;
    setSaving(true);
    try {
      // Clean up options if not select
      const cleanedFields = schemaFields.map(f => {
        if (f.type !== 'select' && f.type !== 'radio') {
          return { ...f, options: [] };
        }
        // Ensure options is an array
        let opts = f.options;
        if (typeof opts === 'string') {
          opts = opts.split(',').map(s => s.trim()).filter(s => s);
        }
        return { ...f, options: opts };
      });

      const moduleKey = activeModule.key || 'generalFields';
      await api.put(`/domain/schema/${selectedIndustry.id}/${moduleKey}`, { fields: cleanedFields });
      setSchemaFields(cleanedFields);
      toast.success(`Schema for ${activeModule.label} saved successfully!`);
    } catch (error) {
      toast.error('Failed to save schema');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 100px)' }}>
        {!selectedIndustry ? (
          <>
            {/* Header with Scope Badge */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--text-primary)', margin: '0 0 6px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <HiOutlineDatabase style={{ color: 'var(--primary, #f97316)' }} />
                  {isPlatformSuperAdmin ? '🌐 Global Domain Builder (Platform Super Admin)' : `🏢 Domain Builder — ${orgName}`}
                </h2>
                <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.95rem' }}>
                  {isPlatformSuperAdmin 
                    ? 'Super Admin View: Select any industry to customize global default form schemas across all SaaS organizations.'
                    : `Organization Admin View: Customize form structures and custom fields exclusively for ${orgName}.`}
                </p>
              </div>

              {/* Security & Access Scope Badge */}
              <div style={{
                padding: '8px 16px',
                borderRadius: '20px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.85rem',
                fontWeight: 600,
                background: isPlatformSuperAdmin ? 'rgba(99, 102, 241, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                color: isPlatformSuperAdmin ? '#818cf8' : '#22c55e',
                border: `1px solid ${isPlatformSuperAdmin ? 'rgba(99, 102, 241, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`
              }}>
                {isPlatformSuperAdmin ? (
                  <>
                    <HiOutlineShieldCheck style={{ fontSize: '1.1rem' }} />
                    Platform Admin Scope: All Tenants Visible
                  </>
                ) : (
                  <>
                    <HiOutlineLockClosed style={{ fontSize: '1.1rem' }} />
                    Isolated Tenant Scope: {orgName} Only
                  </>
                )}
              </div>
            </div>

            {/* Notice banner for Org Admin */}
            {!isPlatformSuperAdmin && (
              <div style={{
                background: 'rgba(249, 115, 22, 0.08)',
                border: '1px solid rgba(249, 115, 22, 0.25)',
                borderRadius: '10px',
                padding: '12px 18px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: 'var(--text-primary)'
              }}>
                <HiOutlineOfficeBuilding style={{ fontSize: '1.4rem', color: 'var(--primary, #f97316)', flexShrink: 0 }} />
                <div style={{ fontSize: '0.9rem' }}>
                  You are viewing the custom domain for <strong>{orgName}</strong> ({settings?.subIndustry || 'Drag Chain'}). Changes made here apply only to forms within your organization.
                </div>
              </div>
            )}

            {/* Industry / Organization Cards Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
              {industries.map(ind => (
                <div 
                  key={ind.id} 
                  onClick={() => openIndustry(ind)}
                  className="glass-card" 
                  style={{ 
                    padding: '24px', 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '16px', 
                    transition: 'var(--transition-fast)',
                    border: ind.isOrgSpecific ? `2px solid ${ind.color || 'var(--primary)'}` : '1px solid var(--border)'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div style={{ 
                    width: '54px', 
                    height: '54px', 
                    borderRadius: '12px', 
                    background: `${ind.color}20`, 
                    color: ind.color, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '1.6rem',
                    flexShrink: 0
                  }}>
                    {ind.isOrgSpecific ? <HiOutlineOfficeBuilding /> : <HiOutlineDatabase />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ind.label}
                      </h3>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Domain ID: {ind.id}</span>
                      {ind.isOrgSpecific && (
                        <span style={{ fontSize: '0.75rem', background: `${ind.color}25`, color: ind.color, padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                          Your Org
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '20px 32px', background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: `${selectedIndustry.color}20`, color: selectedIndustry.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                  <HiOutlineDatabase />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.4rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {selectedIndustry.label} Builder
                    {!isPlatformSuperAdmin && (
                      <span style={{ fontSize: '0.75rem', background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', padding: '3px 10px', borderRadius: '12px', fontWeight: 600 }}>
                        {orgName} Scope
                      </span>
                    )}
                  </h2>
                  <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>Custom Object Schema Configuration & Form Builder</div>
                </div>
              </div>
              <button 
                onClick={closeIndustry} 
                title="Back to Industry / Org Selection"
                style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: 'var(--text-secondary)', width: '40px', height: '40px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}
              >
                <HiOutlineX />
              </button>
            </div>

            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
              {/* Sidebar Modules */}
              <div style={{ width: '280px', borderRight: '1px solid var(--border-light)', padding: '20px', overflowY: 'auto', background: 'rgba(255,255,255,0.01)' }}>
                <h4 style={{ margin: '0 0 16px 0', color: 'var(--text-muted)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Available Modules</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {modules.map(m => (
                    <div key={m.key} style={{ marginBottom: '4px' }}>
                      <button 
                        onClick={() => toggleMenu(m.key)}
                        style={{ 
                          width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', textAlign: 'left',
                          background: expandedMenu === m.key ? `${selectedIndustry.color}15` : 'transparent',
                          color: expandedMenu === m.key ? selectedIndustry.color : 'var(--text-primary)',
                          fontWeight: 600,
                          borderLeft: expandedMenu === m.key ? `3px solid ${selectedIndustry.color}` : '3px solid transparent',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}
                      >
                        {m.label}
                        <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>{expandedMenu === m.key ? '▼' : '▶'}</span>
                      </button>
                      {expandedMenu === m.key && (
                        <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: '24px', marginTop: '8px', gap: '4px' }}>
                          {m.subModules.map(sub => (
                            <button
                              key={sub.key}
                              onClick={() => handleModuleChange(m, sub)}
                              style={{
                                padding: '8px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', textAlign: 'left',
                                background: activeModule?.key === sub.key ? `${selectedIndustry.color}15` : 'transparent',
                                color: activeModule?.key === sub.key ? selectedIndustry.color : 'var(--text-secondary)',
                                fontWeight: activeModule?.key === sub.key ? 500 : 400,
                                fontSize: '0.9rem',
                                transition: 'var(--transition-fast)'
                              }}
                            >
                              {sub.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Builder Area */}
              <div style={{ flex: 1, padding: '32px', overflowY: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                  <h3 style={{ margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {activeModule ? (
                      <>
                        <span style={{ color: 'var(--text-dim)', fontSize: '0.9em' }}>{activeModule.parent}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8em' }}>/</span>
                        <span>{activeModule.label}</span>
                      </>
                    ) : 'Form Fields Configuration'}
                  </h3>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button className="btn btn-secondary" onClick={handleAddField} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <HiOutlinePlus /> Add New Field
                    </button>
                    <button className="btn btn-primary" onClick={handleSaveSchema} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <HiOutlineSave /> {saving ? 'Saving...' : 'Save Schema'}
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading Schema...</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {schemaFields.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '60px', border: '1px dashed var(--border-light)', borderRadius: '12px', color: 'var(--text-dim)' }}>
                        No custom fields configured for this module yet. Click <strong>"Add New Field"</strong> to build your custom form.
                      </div>
                    ) : (
                      schemaFields.map((field, index) => (
                        <div key={index} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-light)', borderRadius: '12px', padding: '20px', display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                          
                          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>Display Label *</label>
                              <input className="form-input" value={field.label} onChange={(e) => handleFieldChange(index, 'label', e.target.value)} placeholder="e.g. Model Number" />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>Data Key (Unique) *</label>
                              <input className="form-input" value={field.key} onChange={(e) => handleFieldChange(index, 'key', e.target.value)} placeholder="e.g. modelNumber" />
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>Field Type *</label>
                              <select className="form-input" value={field.type} onChange={(e) => handleFieldChange(index, 'type', e.target.value)}>
                                <option value="text">Short Text</option>
                                <option value="number">Number</option>
                                <option value="select">Dropdown (Select)</option>
                                <option value="radio">Radio Buttons</option>
                                <option value="checkbox">Checkbox</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>Placeholder text</label>
                              <input className="form-input" value={field.placeholder || ''} onChange={(e) => handleFieldChange(index, 'placeholder', e.target.value)} placeholder="e.g. Enter value..." disabled={field.type === 'checkbox' || field.type === 'radio'} />
                            </div>

                            {(field.type === 'select' || field.type === 'radio') && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>Options (Comma separated)</label>
                                <input 
                                  className="form-input" 
                                  value={Array.isArray(field.options) ? field.options.join(', ') : field.options} 
                                  onChange={(e) => handleFieldChange(index, 'options', e.target.value)} 
                                  placeholder="e.g. Option 1, Option 2, Option 3" 
                                />
                              </div>
                            )}
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '100px', borderLeft: '1px solid var(--border-light)', paddingLeft: '20px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                              <input type="checkbox" checked={field.required} onChange={(e) => handleFieldChange(index, 'required', e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
                              Required
                            </label>
                            <button onClick={() => handleRemoveField(index)} style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              <HiOutlineTrash /> Remove
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DomainBuilder;

